import { useEffect, useState } from "react";
import { Banknote, Clock, CreditCard, MapPin, Minus, Plus, ShieldCheck, ShoppingBag, Truck } from "lucide-react";
import { toast } from "sonner";
import { CATALOG, ORDER_ITEMS, slugify } from "@/data";
import { Reveal, MaskLine } from "@/components/Reveal";
import { MAX_QTY, useBasket } from "@/context/BasketContext";
import { useAuth } from "@/context/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SLOTS = ["10:00", "10:30", "11:00", "11:30", "12:00", "12:30"];

const GATEWAY_META = {
  razorpay: { label: "Razorpay", desc: "UPI (GPay, PhonePe, BHIM) · Cards · Paytm · Netbanking" },
  paytm: { label: "Paytm", desc: "Paytm balance · UPI · cards — secure Paytm checkout" },
  phonepe: { label: "PhonePe", desc: "UPI · cards — secure PhonePe checkout" },
  easebuzz: { label: "Easebuzz", desc: "UPI · cards · netbanking — secure Easebuzz checkout" },
  cashfree: { label: "Cashfree", desc: "UPI · cards · netbanking · wallets — secure Cashfree checkout" },
};

const loaded = new Set();
const loadScript = (src, globalKey) =>
  new Promise((resolve) => {
    if (window[globalKey]) return resolve(true);
    if (loaded.has(src)) return resolve(false);
    loaded.add(src);
    const s = document.createElement("script");
    s.src = src;
    s.crossOrigin = "anonymous";
    s.onload = () => resolve(true);
    s.onerror = () => resolve(false);
    document.body.appendChild(s);
  });

export const OrderSection = () => {
  const { basket: qty, bump, clear, count, total, products } = useBasket();
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");  const [slot, setSlot] = useState("10:00");
  const [note, setNote] = useState("");
  const [fulfillment, setFulfillment] = useState("pickup");
  const [address, setAddress] = useState("");
  const [landmark, setLandmark] = useState("");
  const [city, setCity] = useState("Srinagar");
  const [pincode, setPincode] = useState("190018");
  const [pay, setPay] = useState("cash");
  const [gateway, setGateway] = useState("razorpay");
  const [gateways, setGateways] = useState([]);
  const [deliveryFee, setDeliveryFee] = useState(99);
  const [freeAbove, setFreeAbove] = useState(1000);
  const [deliveryNote, setDeliveryNote] = useState("");
  const [leftMap, setLeftMap] = useState({});
  const { user, authFetch } = useAuth();

  useEffect(() => {
    if (user) {
      setName(user.name || "");
      setPhone(user.phone || "");
      setEmail(user.email || "");
    }
  }, [user]);

  // Abandoned-basket tracking: sync basket + contact so one reminder can be sent later
  useEffect(() => {
    const entries = Object.entries(qty);
    if (!entries.length) return;
    const t = setTimeout(() => {
      authFetch("/basket/sync", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          items: qty,
          name: name.trim() || null,
          phone: phone.replace(/\D/g, "") || null,
          email: email.trim() || null,
        }),
      }).catch(() => {});
    }, 3000);
    return () => clearTimeout(t);
  }, [qty, name, phone, email, user, authFetch]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    fetch(`${API}/payments/config`)
      .then((r) => r.json())
      .then((c) => {
        const list = Array.isArray(c.gateways) ? c.gateways : [];
        setGateways(list);
        if (typeof c.delivery_fee === "number") setDeliveryFee(c.delivery_fee);
        if (typeof c.free_delivery_above === "number") setFreeAbove(c.free_delivery_above);
        if (typeof c.delivery_time_note === "string" && c.delivery_time_note) setDeliveryNote(c.delivery_time_note);
        if (!list.length) setPay("cash");
        else if (!list.includes("razorpay")) setGateway(list[0]);
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    fetch(`${API}/bake`)
      .then((r) => r.json())
      .then((b) => setLeftMap(b.left || {}))
      .catch(() => {});
  }, []);

  const liveByName = Object.fromEntries((products || []).map((p) => [p.name, p]));
  const price = (n) => (liveByName[n] ? liveByName[n].price : CATALOG[n]?.price || 0);
  const shown = (n) => (liveByName[n] ? liveByName[n].available !== false : true);
  const basketExtras = Object.keys(CATALOG)
    .filter((n) => !ORDER_ITEMS.some((i) => i.name === n) && CATALOG[n] && shown(n))
    .map((n) => ({ name: n, price: price(n), desc: CATALOG[n].desc }));
  const formItems = [
    ...ORDER_ITEMS.filter((i) => shown(i.name)).map((i) => ({ ...i, price: price(i.name), desc: CATALOG[i.name]?.desc })),
    ...basketExtras,
    ...(products || [])
      .filter((p) => p.custom && p.available !== false && !CATALOG[p.name])
      .map((p) => ({ name: p.name, price: p.price, desc: "" })),
  ];
  const discount = user && user.first_order ? Math.round(total * 0.1) : 0;
  const deliveryCharged = fulfillment === "delivery" && total < freeAbove;
  const grandTotal = total - discount + (deliveryCharged ? deliveryFee : 0);

  const resetForm = () => {
    clear();
    authFetch("/basket/complete", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ phone: phone.replace(/\D/g, "") || null, email: email.trim() || null }),
    }).catch(() => {});
    setName("");
    setPhone("");
    setEmail("");
    setNote("");
    setAddress("");
    setLandmark("");
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!name.trim() || phone.replace(/\D/g, "").length < 10) {
      toast.error("Please add your name and a valid phone number.");
      return;
    }
    if (email.trim() && !/\S+@\S+\.\S+/.test(email)) {
      toast.error("That email doesn't look right — or leave it empty.");
      return;
    }
    if (count === 0) {
      toast.error("Add at least one item to your basket.");
      return;
    }
    if (fulfillment === "delivery") {
      if (!address.trim() || address.trim().length < 8) {
        toast.error("Please add your full delivery address.");
        return;
      }
      if (!/^\d{6}$/.test(pincode.trim())) {
        toast.error("Please add a valid 6-digit pincode.");
        return;
      }
    }
    setBusy(true);
    try {
      const res = await authFetch("/orders", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          customer_name: name,
          phone,
          email: email.trim() || null,
          items: Object.entries(qty).map(([n, q]) => ({ name: n, qty: q })),
          pickup_date: new Date(Date.now() + 86400000).toISOString().slice(0, 10),
          pickup_slot: slot,
          note: note.trim() || null,
          payment_method: pay,
          gateway: pay === "online" ? gateway : "razorpay",
          fulfillment,
          delivery_address:
            fulfillment === "delivery"
              ? { line: address.trim(), landmark: landmark.trim() || null, city: city.trim(), pincode: pincode.trim() }
              : null,
        }),
      });
      if (!res.ok) {
        let detail = "";
        try {
          const err = await res.json();
          if (err && typeof err.detail === "string") detail = err.detail;
        } catch (e) {
          /* non-JSON error body */
        }
        const e = new Error(detail || "Something went wrong — please try again.");
        e.showDetail = true;
        throw e;
      }
      const data = await res.json();
      const arrive =
        fulfillment === "delivery"
          ? `— we'll deliver within ${deliveryNote || "60-90 minutes"} · our team will confirm the exact time`
          : `reserved for ${slot} tomorrow`;
      if (pay === "online" && gateway === "razorpay") {
        const ready = await loadScript("https://checkout.razorpay.com/v1/checkout.js", "Razorpay");
        if (!ready || !data.razorpay_order_id) throw new Error("razorpay unavailable");
        const rzp = new window.Razorpay({
          key: data.key_id,
          amount: data.amount,
          currency: "INR",
          order_id: data.razorpay_order_id,
          name: "French Pie",
          description: fulfillment === "delivery" ? "Delivery order — French Pie Bemina" : "Pickup order — French Pie Bemina",
          prefill: { name, contact: phone, ...(email.trim() ? { email: email.trim() } : {}) },
          theme: { color: "#C85A32" },
          handler: async (r) => {
            try {
              const v = await fetch(`${API}/payments/verify`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                  order_ref: data.id,
                  razorpay_order_id: r.razorpay_order_id,
                  razorpay_payment_id: r.razorpay_payment_id,
                  razorpay_signature: r.razorpay_signature,
                }),
              });
              if (!v.ok) throw new Error("verify failed");
              toast.success(`Merci! ${count} item${count > 1 ? "s" : ""} ${arrive} — payment received.`);
              resetForm();
            } catch {
              toast.error("Payment received, but confirmation failed — we'll verify it manually.");
            }
          },
          modal: {
            ondismiss: () => {
              toast.message("Payment cancelled — nothing was charged. Choose the cash option to finish, or try again.");
              setPay("cash");
            },
          },
        });
        rzp.open();
      } else if (pay === "online" && gateway === "paytm") {
        const r = await authFetch("/payments/paytm/init", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ order_ref: data.id }),
        });
        if (!r.ok) {
          let msg = "Paytm checkout could not start — please try the cash option or another method.";
          try {
            const err = await r.json();
            if (err && typeof err.detail === "string") msg = err.detail;
          } catch (e) {
            /* gateway/CDN error page */
          }
          const e2 = new Error(msg);
          e2.showDetail = true;
          throw e2;
        }
        const p = await r.json();
        const ok = await loadScript(`${p.host}/merchantpgpui/checkoutjs/merchants/${p.mid}.js`, "Paytm");
        if (!ok || !window.Paytm || !window.Paytm.CheckoutJS) throw new Error("Paytm unavailable");
        const config = {
          root: "",
          flow: "DEFAULT",
          data: { orderId: p.paytm_order_id, token: p.txn_token, tokenType: "TXN_TOKEN", amount: p.amount },
          merchant: { mid: p.mid, redirect: false },
          handler: {
            notifyMerchant: function () {
              // Mandatory Paytm handler — keeps the checkout session alive
              return true;
            },
            transactionStatus: async () => {
              try {
                const v = await fetch(`${API}/payments/paytm/verify`, {
                  method: "POST",
                  headers: { "Content-Type": "application/json" },
                  body: JSON.stringify({ order_ref: data.id }),
                });
                const x = await v.json();
                if (x.status === "paid") {
                  toast.success(`Merci! ${count} item${count > 1 ? "s" : ""} ${arrive} — payment received.`);
                  resetForm();
                } else if (x.status === "failed") {
                  toast.error("Payment failed — nothing was charged.");
                } else {
                  toast.message("Payment is processing — we'll confirm shortly.");
                }
              } catch {
                toast.error("Payment received, but confirmation failed — we'll verify it manually.");
              }
            },
          },
        };
        window.Paytm.CheckoutJS.onLoad(function () {
          window.Paytm.CheckoutJS.init(config)
            .then(function () {
              window.Paytm.CheckoutJS.invoke();
            })
            .catch(function () {
              toast.error("Could not open Paytm checkout — please try again.");
            });
        });
      } else if (pay === "online" && gateway === "easebuzz") {
        const r = await authFetch("/payments/easebuzz/init", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ order_ref: data.id }),
        });
        if (!r.ok) {
          let msg = "Easebuzz checkout could not start — please try the cash option or another method.";
          try {
            const err = await r.json();
            if (err && typeof err.detail === "string") msg = err.detail;
          } catch (e) {
            /* gateway/CDN error page */
          }
          const e2 = new Error(msg);
          e2.showDetail = true;
          throw e2;
        }
        const eb = await r.json();
        toast.message("Taking you to Easebuzz's secure checkout…");
        window.location.assign(eb.payment_url);
        return;
      } else if (pay === "online" && gateway === "cashfree") {
        const r = await authFetch("/payments/cashfree/init", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ order_ref: data.id }),
        });
        if (!r.ok) {
          let msg = "Cashfree checkout could not start — please try the cash option or another method.";
          try {
            const err = await r.json();
            if (err && typeof err.detail === "string") msg = err.detail;
          } catch (e) {
            /* gateway/CDN error page */
          }
          const e2 = new Error(msg);
          e2.showDetail = true;
          throw e2;
        }
        const cf = await r.json();
        const ok = await loadScript("https://sdk.cashfree.com/js/v3/cashfree.js", "Cashfree");
        if (!ok || !window.Cashfree) throw new Error("Cashfree unavailable");
        toast.message("Taking you to Cashfree's secure checkout…");
        const cashfree = new window.Cashfree({ mode: "production" });
        cashfree.checkout({ paymentSessionId: cf.payment_session_id, redirectTarget: "_self" });
        return;
      } else if (pay === "online" && gateway === "phonepe") {
        const r = await fetch(`${API}/payments/phonepe/init`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ order_ref: data.id }),
        });
        if (!r.ok) throw new Error("phonepe init failed");
        const p = await r.json();
        window.location.assign(p.redirect_url);
      } else {
        toast.success(`Merci! ${count} item${count > 1 ? "s" : ""} ${arrive}.`);
        resetForm();
      }
    } catch (err) {
      toast.error(
        err && err.showDetail && err.message ? err.message : "Something went wrong — please try again."
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <section id="order" data-testid="order-section" className="py-24 lg:py-36">
      <div className="max-w-7xl mx-auto px-6 lg:px-12 grid grid-cols-1 lg:grid-cols-12 gap-14 lg:gap-12">
        <div className="lg:col-span-5">
          <Reveal>
            <p className="eyebrow mb-5">Order &amp; Checkout</p>
          </Reveal>
          <h2 className="font-display font-medium leading-[1.02] tracking-tight text-4xl sm:text-5xl lg:text-6xl">
            <MaskLine>Reserve tomorrow's</MaskLine>
            <MaskLine delay={0.12}>
              <span className="italic text-[#C85A32]">bake.</span>
            </MaskLine>
          </h2>
          <Reveal>
            <p className="mt-6 text-base leading-relaxed text-[#1C130E]/75 max-w-md">
              Order by 9 pm tonight — collect fresh at the counter from 10 am, or get it delivered to your door across
              Srinagar. No queue, no disappointment — your croissants will be waiting, still warm.
            </p>
            <p className="mt-4 font-mono-accent text-xs tracking-[0.15em] uppercase text-[#1C130E]/50">
              Pickup at Havells Galaxy, Bemina · Delivery across Srinagar
            </p>
          </Reveal>
        </div>
        <Reveal delay={0.1} className="lg:col-span-7">
          <form
            onSubmit={submit}
            data-testid="order-form"
            className="rounded-3xl border border-[rgba(28,19,14,0.12)] bg-[#F8F3EA] p-8 lg:p-10"
          >
            <p className="font-mono-accent text-[10px] tracking-[0.22em] uppercase text-[#1C130E]/55">
              1 · Pick your bakes
            </p>
            <p className="mb-5 text-xs text-[#1C130E]/55 max-w-lg leading-relaxed" data-testid="order-intro">
              Baked tomorrow morning, reserved by you tonight — each card shows what you're
              getting. Up to 10 of each item; all prices in INR, taxes included.
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {formItems.map((item) => {
                const slug = slugify(item.name);
                const q = qty[item.name] || 0;
                const left = leftMap[item.name];
                const cap = left === undefined ? MAX_QTY : Math.min(MAX_QTY, left);
                return (
                  <div
                    key={item.name}
                    data-testid={`order-item-${slug}`}
                    className={`flex items-center justify-between rounded-2xl border px-4 py-3 transition-colors ${
                      q > 0 ? "border-[#C85A32]/50 bg-[#C85A32]/[0.04]" : "border-[rgba(28,19,14,0.12)] bg-[#FDFBF7]/60"
                    }`}
                  >
                    <span className="pr-2 min-w-0">
                      <span className="font-display text-lg leading-tight block truncate">{item.name}</span>
                      <span className="font-mono-accent text-[10px] text-[#1C130E]/50">₹{item.price}</span>
                      {item.desc && (
                        <span className="block text-[11px] text-[#1C130E]/55 leading-snug mt-1 line-clamp-2">
                          {item.desc}
                        </span>
                      )}
                      {left !== undefined && left <= 5 && (
                        <span
                          data-testid={`order-lowstock-${slug}`}
                          className={`mt-1.5 inline-block rounded-full px-2.5 py-0.5 font-mono-accent text-[9px] uppercase tracking-[0.15em] border ${
                            left === 0
                              ? "text-[#1C130E]/55 border-[rgba(28,19,14,0.2)] bg-[#1C130E]/[0.05]"
                              : "text-[#C85A32] border-[#C85A32]/40 bg-[#C85A32]/[0.07]"
                          }`}
                        >
                          {left === 0 ? "Sold out" : `Only ${left} left`}
                        </span>
                      )}
                    </span>
                    <span className="flex items-center gap-2.5 shrink-0">
                      <button
                        type="button"
                        data-testid={`order-item-${slug}-minus`}
                        aria-label={`Remove one ${item.name}`}
                        onClick={() => bump(item.name, -1)}
                        className="grid place-items-center w-7 h-7 rounded-full border border-[rgba(28,19,14,0.25)] hover:border-[#C85A32] hover:text-[#C85A32] transition-colors"
                      >
                        <Minus size={13} />
                      </button>
                      <span data-testid={`order-qty-${slug}`} className="w-5 text-center text-sm font-semibold">
                        {q}
                      </span>
                      <button
                        type="button"
                        data-testid={`order-item-${slug}-plus`}
                        aria-label={`Add one ${item.name}`}
                        onClick={() => bump(item.name, 1)}
                        disabled={q >= cap}
                        className="grid place-items-center w-7 h-7 rounded-full bg-[#1C130E] text-[#FDFBF7] hover:bg-[#C85A32] disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-[#1C130E] transition-colors"
                      >
                        <Plus size={13} />
                      </button>
                    </span>
                  </div>
                );
              })}
            </div>
            <p className="mt-3 text-sm text-[#1C130E]/55" data-testid="order-empty-hint">
              {count === 0
                ? "Tap + to add items to tomorrow's basket."
                : `${count} item${count > 1 ? "s" : ""} · ₹${total} in your basket.`}
            </p>
            <p className="mt-1.5 font-mono-accent text-[10px] tracking-[0.18em] uppercase text-[#1C130E]/45" data-testid="order-tax-note">
              All prices in INR · inclusive of applicable taxes
            </p>
            <div className="mt-9" data-testid="fulfillment-picker" role="radiogroup" aria-label="Pickup or delivery">
              <p className="font-mono-accent text-[10px] tracking-[0.22em] uppercase text-[#1C130E]/55 mb-5">
                2 · Pickup or delivery?
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <button
                  type="button"
                  role="radio"
                  aria-checked={fulfillment === "pickup"}
                  data-testid="fulfill-pickup"
                  onClick={() => setFulfillment("pickup")}
                  className={`flex items-start gap-3 rounded-2xl border px-4 py-4 text-left transition-colors ${
                    fulfillment === "pickup"
                      ? "border-[#C85A32] bg-[#C85A32]/[0.04]"
                      : "border-[rgba(28,19,14,0.12)] bg-[#FDFBF7]/60 hover:border-[#C85A32]/50"
                  }`}
                >
                  <ShoppingBag size={18} className="text-[#C85A32] shrink-0 mt-0.5" />
                  <span>
                    <span className="font-display text-lg leading-tight block">Store pickup</span>
                    <span className="text-xs text-[#1C130E]/55">Havells Galaxy, Bemina · free</span>
                  </span>
                </button>
                <button
                  type="button"
                  role="radio"
                  aria-checked={fulfillment === "delivery"}
                  data-testid="fulfill-delivery"
                  onClick={() => setFulfillment("delivery")}
                  className={`flex items-start gap-3 rounded-2xl border px-4 py-4 text-left transition-colors ${
                    fulfillment === "delivery"
                      ? "border-[#C85A32] bg-[#C85A32]/[0.04]"
                      : "border-[rgba(28,19,14,0.12)] bg-[#FDFBF7]/60 hover:border-[#C85A32]/50"
                  }`}
                >
                  <Truck size={18} className="text-[#C85A32] shrink-0 mt-0.5" />
                  <span>
                    <span className="font-display text-lg leading-tight block">Home delivery</span>
                    <span className="text-xs text-[#1C130E]/55">
                      Across Srinagar · ₹{deliveryFee} · FREE over ₹{freeAbove.toLocaleString("en-IN")}
                    </span>
                  </span>
                </button>
              </div>
            </div>
            {fulfillment === "delivery" && (
              <div className="mt-7" data-testid="delivery-address-fields">
                <div
                  data-testid="delivery-time-note"
                  className="mb-6 flex items-start gap-2.5 rounded-xl border border-[#D4A359]/40 bg-[#D4A359]/[0.08] px-4 py-3"
                >
                  <Clock size={15} className="text-[#C85A32] shrink-0 mt-0.5" />
                  <p className="text-xs leading-relaxed text-[#1C130E]/75">
                    <span className="font-semibold text-[#1C130E]">Delivery time is set by us:</span> we
                    deliver within {deliveryNote || "60-90 minutes"} — our team confirms the exact time once
                    your order is placed.
                  </p>
                </div>
                <p className="font-mono-accent text-[10px] tracking-[0.22em] uppercase text-[#1C130E]/55 mb-5">
                  Delivery address
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-7">
                  <label className="block sm:col-span-2">
                    <span className="font-mono-accent text-[10px] tracking-[0.22em] uppercase text-[#1C130E]/55">
                      Full address *
                    </span>
                    <textarea
                      rows="2"
                      className="field mt-1 resize-none"
                      data-testid="delivery-address-input"
                      placeholder="House / flat, street, area…"
                      required
                      value={address}
                      onChange={(e) => setAddress(e.target.value)}
                    />
                  </label>
                  <label className="block">
                    <span className="font-mono-accent text-[10px] tracking-[0.22em] uppercase text-[#1C130E]/55">
                      Landmark (optional)
                    </span>
                    <input
                      className="field mt-1"
                      data-testid="delivery-landmark-input"
                      placeholder="Near …"
                      value={landmark}
                      onChange={(e) => setLandmark(e.target.value)}
                    />
                  </label>
                  <label className="block">
                    <span className="font-mono-accent text-[10px] tracking-[0.22em] uppercase text-[#1C130E]/55">
                      City *
                    </span>
                    <input
                      className="field mt-1"
                      data-testid="delivery-city-input"
                      placeholder="Srinagar"
                      required
                      value={city}
                      onChange={(e) => setCity(e.target.value)}
                    />
                  </label>
                  <label className="block">
                    <span className="font-mono-accent text-[10px] tracking-[0.22em] uppercase text-[#1C130E]/55">
                      Pincode *
                    </span>
                    <input
                      className="field mt-1"
                      data-testid="delivery-pincode-input"
                      placeholder="190018"
                      required
                      inputMode="numeric"
                      maxLength="6"
                      value={pincode}
                      onChange={(e) => setPincode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                    />
                  </label>
                </div>
              </div>
            )}
            <p className="font-mono-accent text-[10px] tracking-[0.22em] uppercase text-[#1C130E]/55 mt-9 mb-5">
              3 · Your details
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-7">
              <label className="block">
                <span className="font-mono-accent text-[10px] tracking-[0.22em] uppercase text-[#1C130E]/55">Name *</span>
                <input
                  className="field mt-1"
                  data-testid="order-name-input"
                  placeholder="Your name"
                  required
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                />
              </label>
              <label className="block">
                <span className="font-mono-accent text-[10px] tracking-[0.22em] uppercase text-[#1C130E]/55">Phone *</span>
                <input
                  className="field mt-1"
                  data-testid="order-phone-input"
                  placeholder="+91 …"
                  required
                  type="tel"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                />
              </label>
              <label className="block">
                <span className="font-mono-accent text-[10px] tracking-[0.22em] uppercase text-[#1C130E]/55">
                  Email — for confirmation
                </span>
                <input
                  className="field mt-1"
                  data-testid="order-email-input"
                  placeholder="you@example.com"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
              </label>
              {fulfillment === "pickup" && (
                <label className="block">
                  <span className="font-mono-accent text-[10px] tracking-[0.22em] uppercase text-[#1C130E]/55">
                    Pickup slot (tomorrow)
                  </span>
                  <select
                    className="field mt-1"
                    data-testid="order-slot-select"
                    value={slot}
                    onChange={(e) => setSlot(e.target.value)}
                  >
                    {SLOTS.map((s) => (
                      <option key={s} value={s}>
                        {s}
                      </option>
                    ))}
                  </select>
                </label>
              )}
              <label className="block sm:col-span-2">
                <span className="font-mono-accent text-[10px] tracking-[0.22em] uppercase text-[#1C130E]/55">
                  Note (optional)
                </span>
                <input
                  className="field mt-1"
                  data-testid="order-note-input"
                  placeholder="Birthday message, extra flaky…"
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                />
              </label>
            </div>
            <div className="mt-9" data-testid="payment-method-picker" role="radiogroup" aria-label="Payment method">
              <p className="font-mono-accent text-[10px] tracking-[0.22em] uppercase text-[#1C130E]/55 mb-5">
                4 · Payment
              </p>
              <div className={`grid gap-3 ${gateways.length ? "grid-cols-1 sm:grid-cols-2" : "grid-cols-1"}`}>
                <button
                  type="button"
                  role="radio"
                  aria-checked={pay === "cash"}
                  data-testid="pay-method-cash"
                  onClick={() => setPay("cash")}
                  className={`flex items-start gap-3 rounded-2xl border px-4 py-4 text-left transition-colors ${
                    pay === "cash"
                      ? "border-[#C85A32] bg-[#C85A32]/[0.04]"
                      : "border-[rgba(28,19,14,0.12)] bg-[#FDFBF7]/60 hover:border-[#C85A32]/50"
                  }`}
                >
                  <Banknote size={18} className="text-[#C85A32] shrink-0 mt-0.5" />
                  <span>
                    <span className="font-display text-lg leading-tight block">
                      {fulfillment === "delivery" ? "Cash on delivery" : "Cash on pickup"}
                    </span>
                    <span className="text-xs text-[#1C130E]/55">
                      {fulfillment === "delivery" ? "Pay our rider at your door" : "Pay at the counter when you collect"}
                    </span>
                  </span>
                </button>
                {gateways.map((g) =>
                  GATEWAY_META[g] ? (
                    <button
                      key={g}
                      type="button"
                      role="radio"
                      aria-checked={pay === "online" && gateway === g}
                      data-testid={`pay-method-${g}`}
                      onClick={() => {
                        setPay("online");
                        setGateway(g);
                      }}
                      className={`flex items-start gap-3 rounded-2xl border px-4 py-4 text-left transition-colors ${
                        pay === "online" && gateway === g
                          ? "border-[#C85A32] bg-[#C85A32]/[0.04]"
                          : "border-[rgba(28,19,14,0.12)] bg-[#FDFBF7]/60 hover:border-[#C85A32]/50"
                      }`}
                    >
                      <span className="shrink-0 grid place-items-center w-10 h-10 rounded-lg border border-[rgba(28,19,14,0.1)] bg-white overflow-hidden">
                        <span
                          className={`font-bold text-[11px] tracking-tight ${g === "paytm" ? "italic" : ""}`}
                          style={{ color: g === "paytm" ? "#00BAF2" : g === "easebuzz" ? "#6C4CF1" : g === "cashfree" ? "#D63847" : "#1C130E" }}
                        >
                          {GATEWAY_META[g].label}
                        </span>
                      </span>
                      <span>
                        <span className="font-display text-lg leading-tight block">{GATEWAY_META[g].label}</span>
                        <span className="text-xs text-[#1C130E]/55">{GATEWAY_META[g].desc}</span>
                      </span>
                    </button>
                  ) : null
                )}
              </div>
              <div className="mt-5 flex items-center gap-2 flex-wrap" data-testid="accepted-payments">
                <span className="inline-flex items-center gap-1.5 text-xs text-[#1C130E]/55 mr-1">
                  <ShieldCheck size={14} className="text-green-700" /> 100% secure · we accept
                </span>
                {["Paytm", "UPI", "GPay", "PhonePe", "VISA", "Mastercard", "RuPay", "NetBanking"].map((b) => (
                  <span
                    key={b}
                    className="rounded-md border border-[rgba(28,19,14,0.14)] bg-white px-2 py-1 text-[10px] font-bold text-[#1C130E]/70"
                  >
                    {b}
                  </span>
                ))}
              </div>
            </div>
            <div
              className="mt-7 rounded-2xl border border-[rgba(28,19,14,0.12)] bg-[#FDFBF7]/60 px-5 py-4 text-sm"
              data-testid="order-summary"
            >
              <div className="flex justify-between text-[#1C130E]/70">
                <span>
                  {count} item{count === 1 ? "" : "s"}
                </span>
                <span className="font-mono-accent">₹{total}</span>
              </div>
              {fulfillment === "delivery" && (
                <div className="flex justify-between text-[#1C130E]/70 mt-1.5">
                  <span>
                    {deliveryCharged ? "Delivery · across Srinagar" : "Delivery · FREE (order over ₹1,000)"}
                  </span>
                  <span className="font-mono-accent" data-testid="delivery-fee-line">
                    {deliveryCharged ? `₹${deliveryFee}` : "FREE"}
                  </span>
                </div>
              )}
              {discount > 0 && (
                <div className="flex justify-between text-[#C85A32] mt-1.5" data-testid="welcome-offer-line">
                  <span>Bonjour offer · 10% off your first order</span>
                  <span className="font-mono-accent">−₹{discount}</span>
                </div>
              )}
              <div className="flex justify-between font-semibold text-[#1C130E] mt-1.5 pt-1.5 border-t border-[rgba(28,19,14,0.12)]">
                <span>Total (taxes included)</span>
                <span className="font-mono-accent" data-testid="order-total">
                  ₹{grandTotal}
                </span>
              </div>
            </div>
            <button
              type="submit"
              data-testid="order-submit-btn"
              disabled={busy}
              className="group mt-9 inline-flex items-center justify-center gap-2 rounded-full bg-[#1C130E] text-[#FDFBF7] px-8 py-4 text-sm font-semibold hover:bg-[#C85A32] disabled:opacity-60 disabled:cursor-not-allowed transition-colors w-full sm:w-auto"
            >
              {busy
                ? "Reserving…"
                : count > 0
                  ? pay === "online"
                    ? `Pay ₹${grandTotal} via ${GATEWAY_META[gateway] ? GATEWAY_META[gateway].label : "UPI"}`
                    : fulfillment === "delivery"
                      ? `Order ${count} item${count > 1 ? "s" : ""} · ₹${grandTotal}`
                      : `Reserve ${count} item${count > 1 ? "s" : ""} · ₹${grandTotal}`
                  : fulfillment === "delivery"
                    ? "Order for delivery"
                    : "Reserve for pickup"}
              <ShoppingBag size={16} className="transition-transform duration-300 group-hover:scale-110" />
            </button>
          </form>
        </Reveal>
      </div>
    </section>
  );
};
